/**
 * 模型定价表与费用估算，逐字移植自旧版 www/legacy/cost.html (PRICES / calcCostByModel / calcCost)。
 * 单位：USD / 1M tokens。input / cached_input / output；cached 为 null 表示无缓存价。
 * 费用 = input_tokens / 1e6 × input + output_tokens / 1e6 × output（不计缓存价、不计图片）。
 */

export interface ModelPrice {
  model: string;
  input: number;
  cached: number | null;
  output: number;
}

export const PRICES: ModelPrice[] = [
  { model: "gpt-5.4",           input: 2.50,  cached: 0.25,   output: 15.00  },
  { model: "gpt-5.4-mini",      input: 0.75,  cached: 0.075,  output: 4.50   },
  { model: "gpt-5.4-nano",      input: 0.20,  cached: 0.02,   output: 1.25   },
  { model: "gpt-5.4-pro",       input: 30.00, cached: null,   output: 180.00 },
  { model: "gpt-5.2",           input: 1.75,  cached: 0.175,  output: 14.00  },
  { model: "gpt-5.2-pro",       input: 21.00, cached: null,   output: 168.00 },
  { model: "gpt-5.1",           input: 1.25,  cached: 0.125,  output: 10.00  },
  { model: "gpt-5",             input: 1.25,  cached: 0.125,  output: 10.00  },
  { model: "gpt-5-mini",        input: 0.25,  cached: 0.025,  output: 2.00   },
  { model: "gpt-5-nano",        input: 0.05,  cached: 0.005,  output: 0.40   },
  { model: "gpt-5-pro",         input: 15.00, cached: null,   output: 120.00 },
  { model: "gpt-4.1",           input: 2.00,  cached: 0.50,   output: 8.00   },
  { model: "gpt-4.1-mini",      input: 0.40,  cached: 0.10,   output: 1.60   },
  { model: "gpt-4.1-nano",      input: 0.10,  cached: 0.025,  output: 0.40   },
  { model: "gpt-4o",            input: 2.50,  cached: 1.25,   output: 10.00  },
  { model: "gpt-4o-mini",       input: 0.15,  cached: 0.075,  output: 0.60   },
  { model: "o4-mini",           input: 1.10,  cached: 0.275,  output: 4.40   },
  { model: "o3",                input: 2.00,  cached: 0.50,   output: 8.00   },
  { model: "o3-mini",           input: 1.10,  cached: 0.55,   output: 4.40   },
  { model: "o3-pro",            input: 20.00, cached: null,   output: 80.00  },
  { model: "o1",                input: 15.00, cached: 7.50,   output: 60.00  },
  { model: "o1-mini",           input: 1.10,  cached: 0.55,   output: 4.40   },
  { model: "o1-pro",            input: 150.00,cached: null,   output: 600.00 },
  { model: "gpt-4o-2024-05-13", input: 5.00,  cached: null,   output: 15.00  },
  { model: "gpt-4-turbo",       input: 10.00, cached: null,   output: 30.00  },
  { model: "gpt-3.5-turbo",     input: 0.50,  cached: null,   output: 1.50   },
  { model: "gpt-3.5-turbo-instruct", input: 1.50, cached: null, output: 2.00 },
  { model: "davinci-002",       input: 2.00,  cached: null,   output: 2.00   },
  { model: "babbage-002",       input: 0.40,  cached: null,   output: 0.40   },
  // Doubao / Ark models (approx, CNY converted ~7.1)
  { model: "doubao-pro-4k",     input: 0.11,  cached: null,   output: 0.28   },
  { model: "doubao-pro-32k",    input: 0.80,  cached: null,   output: 2.00   },
  { model: "doubao-pro-128k",   input: 4.00,  cached: null,   output: 8.00   },
  { model: "doubao-lite-4k",    input: 0.04,  cached: null,   output: 0.07   },
  { model: "doubao-lite-32k",   input: 0.18,  cached: null,   output: 0.25   },
  { model: "doubao-lite-128k",  input: 0.80,  cached: null,   output: 1.00   },
];

/** 汇总表（按天 / 按月，没有逐行模型）默认用来估算的模型 */
export const DEFAULT_MODEL = "gpt-4o";

/** 选择的模型在表里找不到时的兜底价（同旧版 getPrice） */
const FALLBACK_PRICE: ModelPrice = { model: DEFAULT_MODEL, input: 2.50, cached: 1.25, output: 10.00 };

export function findPrice(model: string | null | undefined): ModelPrice | undefined {
  return PRICES.find((p) => p.model === model);
}

/** 当前选择模型的价格（找不到则兜底） */
export function selectedPrice(selectedModel: string): ModelPrice {
  return findPrice(selectedModel) ?? FALLBACK_PRICE;
}

interface TokenRow {
  input_tokens?: number | null;
  output_tokens?: number | null;
}

/** 按指定模型名精准算费，找不到定价则 fallback 到当前选择的模型 */
export function calcCostByModel(
  row: TokenRow,
  modelName: string | null | undefined,
  selectedModel: string,
): { cost: number; price: ModelPrice } {
  const p = findPrice(modelName) ?? selectedPrice(selectedModel);
  const inputCost = (row.input_tokens || 0) / 1e6 * p.input;
  const outputCost = (row.output_tokens || 0) / 1e6 * p.output;
  return { cost: inputCost + outputCost, price: p };
}

/** 汇总行（无模型字段）用当前选择的模型估算 */
export function calcCost(row: TokenRow, selectedModel: string): number {
  return calcCostByModel(row, selectedModel, selectedModel).cost;
}

export function fmtNum(n: number | null | undefined): string {
  return n == null ? "—" : Number(n).toLocaleString();
}

export function fmtCost(c: number | null | undefined): string {
  return c == null ? "—" : "$" + c.toFixed(4);
}
